/**
 * Format: Option
 * -----------------------------------------------------------------------------
 * Formats Shopify storefront API options response.
 *
 */
import { formatMoney } from '@we-make-websites/theme-currency'
import { getDisabledColorCodes } from '~/helpers/product'

/**
 * Format product options array.
 * @param {Object} product - Product response.
 * @param {Array} variants - Formatted variants response.
 * @returns {Array}
 */
export function formatOptions(product, variants) {
  return product.options.map((option, index) => {
    return formatOption(option, { index, product, variants })
  })
}

/**
 * Format product option.
 * - https://shopify.dev/docs/api/storefront/2023-07/objects/ProductOption
 * @param {Object} option - Option response.
 * @param {Object} [data] - Additional data.
 * @param {Number} [data.index] - Forloop index.
 * @param {Object} [data.product] - Product response.
 * @param {Array} [data.variants] - Formatted variants response.
 * @returns {Object}
 */
export function formatOption(option, { index = 0, product, variants } = {}) {
  const formattedOption = {
    index,
    labels: option.values,
    name: option.name,
    values: option.values,
  }

  /**
   * If gift card then show price as label to support multi-currency.
   */
  if (product.giftCard && variants.length) {
    formattedOption.labels = option.values.map((_, valueIndex) => {
      return formatMoney(variants[valueIndex].price.amount, cnvs.store.moneyFormat)
    })
  }

  return formattedOption
}

/**
 * Get filtered color options data, with disabled colors removed.
 * @param {Object} product - Product object
 * @returns {Object|null}
 */
export function getFilteredColorOptions(product, colorOptions) {
  const disabled = getDisabledColorCodes(product.tags)
  const disabledIndices = colorOptions.colorCodes.map((code, index) => {
    if (!disabled.includes(code)) {
      return null
    }

    return index
  }).filter(item => item !== null)

  if (disabledIndices.length === colorOptions.colorCodes.length) {
    return null
  }

  if (disabledIndices.length) {
    filterColorOptions(colorOptions, disabledIndices)
  }

  return { ...colorOptions }

}

/**
 * Filter color options.
 * @param {Object} colorOptions - Product's original colorOptions object
 * @param {Array} disabledIndices - Indices of color codes disabled by tag
 * @returns {Object}
 */
export function filterColorOptions(colorOptions, disabledIndices) {
  for (const key in colorOptions) {
    if (!Array.isArray(colorOptions[key])) {
      continue
    }

    colorOptions[key] = colorOptions[key].filter((_, index) => {
      return !disabledIndices.includes(index)
    })
  }
}

/**
 * Format color options array to mimic custom Liquid formatter.
 * @param {Object} product - Product response.
 * @param {Array} variants - Formatted variants response.
 * @returns {Array}
 */
export function formatColorOptions(product, variants) {
  const colorOptionIndex = product.options.findIndex((option) => option.name === 'Color')

  if (colorOptionIndex === -1) {
    return []
  }

  const colorOption = product.options[colorOptionIndex]

  const formattedColorOptions = formatOption(
    colorOption,
    { colorOptionIndex, product, variants },
  )

  formattedColorOptions.colorCodes = []
  formattedColorOptions.colorLabels = []
  formattedColorOptions.handle = product.handle

  formattedColorOptions.values.forEach((value) => {
    const splitCode = value.split('-')

    formattedColorOptions.colorCodes.push(splitCode[0]?.trim())
    formattedColorOptions.colorLabels.push(splitCode[1]?.trim() || '')
  })

  const validColorOptions = getFilteredColorOptions(product, formattedColorOptions)

  return [{ ...validColorOptions }]
}
